import * as React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Typography,
  Box,
} from "@mui/material";

const DEFAULT_METADATA = {
  metadataCountry: "US",
  metadataIpAddress: "0.0.0.0",
  metadataUserAgent: "BryteBridge Admin",
  timezone: "America/New_York",
};

export default function CreateSquarespaceCustomerDialog({
  open,
  onClose,
  formData,
  onFormDataChange,
  onCreate,
  creating,
  error,
  apiResponse,
}) {
  const handleClose = () => {
    onFormDataChange({
      email: "",
      firstName: "",
      lastName: "",
      ...DEFAULT_METADATA,
    });
    onClose();
  };

  const isEmailValid = formData.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim());
  const canSubmit = formData.email?.trim() && formData.firstName?.trim() && formData.lastName?.trim() && isEmailValid;
  const isSuccess = apiResponse?.success === true;
  const showResult = !!apiResponse;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
    >
      <DialogTitle>Create Squarespace Customer</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {isSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {apiResponse.message || "Customer created successfully"}
          </Alert>
        )}
        {showResult && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 1,
              backgroundColor: (theme) =>
                theme.palette.mode === "dark" ? theme.palette.grey[900] : theme.palette.grey[100],
              fontFamily: "monospace",
              fontSize: "0.8rem",
              overflow: "auto",
              maxHeight: 200,
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
              API Response
            </Typography>
            <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {JSON.stringify(apiResponse, null, 2)}
            </pre>
          </Box>
        )}
        {(!showResult || !isSuccess) && (
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              required
              value={formData.email || ""}
              onChange={(e) => onFormDataChange({ ...formData, email: e.target.value })}
              margin="dense"
              size="small"
              disabled={creating}
              helperText="Customer email address (required)"
              error={formData.email && !isEmailValid}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="First Name"
              required
              value={formData.firstName || ""}
              onChange={(e) => onFormDataChange({ ...formData, firstName: e.target.value })}
              margin="dense"
              size="small"
              disabled={creating}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Last Name"
              required
              value={formData.lastName || ""}
              onChange={(e) => onFormDataChange({ ...formData, lastName: e.target.value })}
              margin="dense"
              size="small"
              disabled={creating}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5 }}>
              Metadata (required by Squarespace)
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label="Country"
              value={formData.metadataCountry || "US"}
              onChange={(e) => onFormDataChange({ ...formData, metadataCountry: e.target.value })}
              margin="dense"
              size="small"
              disabled={creating}
              helperText="e.g. US"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label="IP Address"
              value={formData.metadataIpAddress || "0.0.0.0"}
              onChange={(e) => onFormDataChange({ ...formData, metadataIpAddress: e.target.value })}
              margin="dense"
              size="small"
              disabled={creating}
              helperText="Customer IP"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label="User Agent"
              value={formData.metadataUserAgent || "BryteBridge Admin"}
              onChange={(e) => onFormDataChange({ ...formData, metadataUserAgent: e.target.value })}
              margin="dense"
              size="small"
              disabled={creating}
              helperText="Browser/user agent string"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Timezone"
              value={formData.timezone || "America/New_York"}
              onChange={(e) => onFormDataChange({ ...formData, timezone: e.target.value })}
              margin="dense"
              size="small"
              disabled={creating}
              helperText="e.g. America/New_York, UTC"
            />
          </Grid>
        </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={creating}>
          {isSuccess ? "Done" : "Cancel"}
        </Button>
        {!isSuccess && (
          <Button 
            onClick={onCreate} 
            variant="contained"
            disabled={!canSubmit || creating}
            startIcon={creating ? <CircularProgress size={16} /> : null}
          >
            {creating ? "Creating..." : "Create Customer"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
